import { Alert, Col, Container, Form, InputGroup, Row, Stack } from "react-bootstrap";
import StationPicker from "../../components/Pickers/StationPicker";
import AnalyzeButton from "../../components/AnalyzeButton";
import { axiosInstance } from "../../auth/Auth";
import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDownFill, CaretUpFill, Search } from "react-bootstrap-icons";
import useSortableData from "../../components/useSortableData";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useLoaderData } from "react-router-dom";
import ContentGroupRow from "../../components/ContentGroupRow";
import { useUserFormState } from "../../components/UserSelection";

export default function SettingsContentGroups() {
    const moduleName = "settingsContentGroups";
    const groupsLimit = 5;
    const { customContentGroups } = useLoaderData();

    const [formData, setFormData] = useUserFormState(moduleName);
    const [filterData, setFilterData] = useState({ station: [], name: "" });

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const tableContainerRef = useRef(null);

    async function handleAddContentGroup(event) {
        event.preventDefault();
        try {
            const inputs = formData.station.map(x => {
                return {
                    radio_id: x.radio_id,
                    name: formData.name,
                };
            });
            const promises = inputs.map(x => axiosInstance.post("content_testing/content_groups/", x));
            const results = await Promise.all(promises.map(p => p.catch(error => ({ error }))));
            const successfulResponses = results.filter(response => !response.error);
            const newContentGroups = successfulResponses.map(response => response.data);
            addContentGroups(newContentGroups);
            setFormData({ station: [], name: "" });
        } catch (error) {}
    }

    useEffect(() => {
        async function getContentGroups() {
            setLoading(true);
            const resolvedContentGroups = (await customContentGroups).data;
            setData(resolvedContentGroups);
            setLoading(false);
        }
        getContentGroups();
    }, [customContentGroups]);

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.station.length > 0) {
                filteredData = filteredData.filter(x => filterData.station.map(s => s.radio_id).includes(x.radio_id));
            }
            if (filterData.name) {
                const searchTerm = filterData.name.toLowerCase().trim();
                filteredData = filteredData.filter(x => x.display_name?.toLowerCase().includes(searchTerm));
            }
            return filteredData;
        }
        return [];
    }, [data, filterData]);

    const limitReachedRadioIds = useMemo(() => {
        if (data) {
            // Use reduce to count occurrences of not disabled radio_id
            const occurrences = data.reduce((acc, obj) => {
                if (!obj.is_disabled) {
                    acc[obj.radio_id] = (acc[obj.radio_id] || 0) + 1;
                }
                return acc;
            }, {});
            // Filter radio_ids that reached limit
            return Object.keys(occurrences)
                .filter(key => occurrences[key] >= groupsLimit)
                .map(Number);
        }
        return [];
    }, [data]);

    const limitReachedStations = useMemo(() => {
        return formData.station.filter(x => limitReachedRadioIds.includes(x.radio_id));
    }, [formData.station, limitReachedRadioIds]);

    const removeContentGroup = id => setData(data.filter(x => x.group_id !== id));
    const addContentGroups = contentGroups => setData([...data, ...contentGroups]);
    const editContentGroup = (id, modifiedContentGroup) => setData(data.map(x => (x.group_id === id ? modifiedContentGroup : x)));

    const { sortedItems, requestSort, sortConfig } = useSortableData(dataMemo, { key: "radio_id", direction: "ascending" });

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: () => 40,
        overscan: 20,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <>
            <Row>
                <Col className="bg-white p-4">
                    <h2 className="text-uppercase">Add Content Groups</h2>
                    <Form onSubmit={handleAddContentGroup} className="row gy-3">
                        <Col xs={12} xl>
                            <Row className="gy-3">
                                <Form.Group as={Col} controlId={`${moduleName}-new-name`} className="col-12 col-xl min-w-220">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.name || ""}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Content group name"
                                    />
                                </Form.Group>
                                <StationPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    filterFunction={x => !x.mtt_only}
                                    multiple={true}
                                    label="Add for station(s)"
                                    colXl={5}
                                />
                                {limitReachedStations.length > 0 && (
                                    <Col xs={12}>
                                        <Alert variant="warning" className="m-0 py-1">
                                            <b>{limitReachedStations.map(x => x.radio_name).join(", ")}</b> reached the maximum limit of <b>{groupsLimit}</b> active (not archived) content groups.
                                        </Alert>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                        <Col xs={12} xl="auto" className="text-center text-xl-end align-self-end">
                            <AnalyzeButton label="Add New" disabled={!formData.station.length || !formData.name || limitReachedStations.length > 0} />
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr />
            <Row>
                <Col className="bg-white p-4">
                    <h2 className=" text-uppercase">Content Groups</h2>
                    {loading ? (
                        <span colSpan={12}>
                            <LoadingIndicator width={75} />
                        </span>
                    ) : (
                        data && (
                            <>
                                <Row className="mb-2 gy-2">
                                    <StationPicker
                                        formData={filterData}
                                        setFormData={setFilterData}
                                        name={moduleName}
                                        multiple={true}
                                        placeholder="Search by station"
                                        label={false}
                                        colXl={6}
                                    />
                                    <Form.Group as={Col} className="col-12 col-xl-6">
                                        <InputGroup>
                                            <InputGroup.Text className="input-icon">
                                                <Search />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Search by name"
                                                id="search_name"
                                                value={filterData.name}
                                                onChange={e => setFilterData({ ...filterData, name: e.target.value })}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Row>
                                <Container fluid>
                                    <Row className="bg-ra-dark-blue text-white fw-bold py-1 rounded-2 fs-8-xs table-header user-select-none">
                                        <Col xs sm={5} onClick={() => requestSort("radio_id")} className=" align-self-center">
                                            <Stack direction="horizontal" className="justify-content-start">
                                                <span className="text-center">Station</span>
                                                <span className="col-1">
                                                    {sortConfig?.key === "radio_id" &&
                                                        (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                                </span>
                                            </Stack>
                                        </Col>
                                        <Col xs onClick={() => requestSort("name")} className=" align-self-center">
                                            <Stack direction="horizontal" className="justify-content-start">
                                                <span className="text-center">Name</span>
                                                <span className="col-1">
                                                    {sortConfig?.key === "name" &&
                                                        (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                                </span>
                                            </Stack>
                                        </Col>
                                    </Row>
                                </Container>
                                <div ref={tableContainerRef}>
                                    <Container fluid className="position-relative" style={{ height: virtualizer.getTotalSize() }}>
                                        {virtualizer.getVirtualItems().map(virtualRow => {
                                            const item = sortedItems[virtualRow.index];
                                            if (!item) {
                                                return (
                                                    <Row
                                                        key={virtualRow.key}
                                                        data-index={virtualRow.index}
                                                        ref={virtualizer.measureElement}
                                                        style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
                                                        className="py-2 border-bottom position-absolute w-100 pe-2">
                                                        <Col className="text-center">No Custom Content groups</Col>
                                                    </Row>
                                                );
                                            }
                                            return (
                                                <Row
                                                    key={item.group_id}
                                                    data-index={virtualRow.index}
                                                    ref={virtualizer.measureElement}
                                                    style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
                                                    className="py-1 border-bottom position-absolute w-100">
                                                    <ContentGroupRow
                                                        contentGroup={item}
                                                        onDelete={removeContentGroup}
                                                        onEdit={editContentGroup}
                                                        limitReached={limitReachedRadioIds.includes(item.radio_id)}
                                                    />
                                                </Row>
                                            );
                                        })}
                                    </Container>
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </>
    );
}
